import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setGraphs,
  setSelectedGraph,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedGraph,
  addGraph,
  updateGraph as updateGraphAction,
  deleteGraph as deleteGraphAction,
} from '../../store/slices/graphsSlice';
import { GET_GRAPHS_LIST, GET_GRAPH } from '../../graphql/queries/graphs.queries';
import { CREATE_GRAPH, UPDATE_GRAPH, DELETE_GRAPH } from '../../graphql/mutations/graphs.mutations';
import {
  FindGraphListInput,
  FindGraphInput,
  CreateGraphInput,
  UpdateGraphInput,
  DeleteGraphInput,
  CreateGraphResponse,
  UpdateGraphResponse,
  DeleteGraphResponse,
  GraphsListResponse,
  GraphResponse,
  Graph,
} from '../../types/graphs.types';
import ViewGraphModal from '../../components/Graphs/ViewGraphModal';
import CreateEditGraphModal, { GraphFormData } from '../../components/Graphs/CreateEditGraphModal';
import ConfirmDeleteGraphDialog from '../../components/Graphs/ConfirmDeleteGraphDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './Graphs.css';

const Graphs: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { graphs, loading, error, pagination, search } = useAppSelector(
    (state) => state.graphs
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [graphToDelete, setGraphToDelete] = useState<Graph | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [fetchGraphs, { loading: fetchingList }] = useLazyQuery<
    { graphs: GraphsListResponse },
    { params: FindGraphListInput }
  >(GET_GRAPHS_LIST, { fetchPolicy: 'network-only' });

  const [fetchGraph] = useLazyQuery<
    { graph: GraphResponse },
    { params: FindGraphInput }
  >(GET_GRAPH, { fetchPolicy: 'network-only' });

  const [createGraphMutation] = useMutation<
    { createGraph: CreateGraphResponse },
    { params: CreateGraphInput }
  >(CREATE_GRAPH);

  const [updateGraphMutation] = useMutation<
    { updateGraph: UpdateGraphResponse },
    { params: UpdateGraphInput }
  >(UPDATE_GRAPH);

  const [deleteGraphMutation] = useMutation<
    { deleteGraph: DeleteGraphResponse },
    { params: DeleteGraphInput }
  >(DELETE_GRAPH);

  useEffect(() => {
    loadGraphs();
  }, [pagination.page, pagination.pageSize, search]);

  const loadGraphs = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchGraphs({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.graphs?.success && data.graphs.data) {
        dispatch(setGraphs(data.graphs.data));
      } else {
        const errorMessage = data?.graphs?.message || 'Error al cargar grafos';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchGraph({
        variables: { params: { ids: [id] } },
      });

      if (data?.graph?.success && data.graph.data) {
        const graph = data.graph.data[0];
        dispatch(setSelectedGraph(graph));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.graph?.message || 'Error al cargar grafo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchGraph({
        variables: { params: { ids: [id] } },
      });

      if (data?.graph?.success && data.graph.data) {
        const graph = data.graph.data[0];
        dispatch(setSelectedGraph(graph));
        setModalMode('edit');
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.graph?.message || 'Error al cargar grafo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedGraph());
    setModalMode('create');
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: GraphFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        const { data: responseData } = await createGraphMutation({
          variables: {
            params: {
              id: formData.id,
              nodes: formData.nodes,
              tables: formData.tables,
              baseTable: formData.baseTable,
              joinCondition: formData.joinCondition,
              businessRules: formData.businessRules,
            },
          },
        });

        if (responseData?.createGraph?.success) {
          if (responseData.createGraph.data) {
            dispatch(addGraph(responseData.createGraph.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedGraph());
          setSaveError(null);
          setSuccessMessage('Grafo creado exitosamente');
        } else {
          const msg = responseData?.createGraph?.message || 'Error al crear grafo';
          const errs = responseData?.createGraph?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await updateGraphMutation({
          variables: {
            params: {
              id: formData.id,
              nodes: formData.nodes,
              tables: formData.tables,
              baseTable: formData.baseTable,
              joinCondition: formData.joinCondition,
              businessRules: formData.businessRules,
            },
          },
        });

        if (responseData?.updateGraph?.success) {
          if (responseData.updateGraph.data) {
            dispatch(updateGraphAction(responseData.updateGraph.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedGraph());
          setSaveError(null);
          setSuccessMessage('Grafo actualizado exitosamente');
        } else {
          const msg = responseData?.updateGraph?.message || 'Error al actualizar grafo';
          const errs = responseData?.updateGraph?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (graph: Graph) => {
    setGraphToDelete(graph);
    setDeleteError(null);
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setGraphToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!graphToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteGraphMutation({
        variables: { params: { ids: [graphToDelete.id] } },
      });

      if (responseData?.deleteGraph?.success) {
        dispatch(deleteGraphAction(graphToDelete.id));
        setConfirmDeleteOpen(false);
        setGraphToDelete(null);
        setSuccessMessage('Grafo eliminado exitosamente');
      } else {
        const msg = responseData?.deleteGraph?.message || 'Error al eliminar grafo';
        const errs = responseData?.deleteGraph?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedGraph());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedGraph());
    setSaveError(null);
    setSaving(false);
  };

  const columns: GridColDef<Graph>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 150,
    },
    {
      field: 'nodes',
      headerName: 'Nodos',
      flex: 1,
      minWidth: 150,
      valueGetter: (value: string[]) => value?.join(', ') || '',
    },
    {
      field: 'tables',
      headerName: 'Tablas',
      flex: 1,
      minWidth: 150,
      valueGetter: (value: string[]) => value?.join(', ') || '',
    },
    {
      field: 'baseTable',
      headerName: 'Tabla Base',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 140,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="graphs-container">
        <Paper elevation={2} className="graphs-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Grafos</Typography>
          </Breadcrumbs>

          <Box className="graphs-header">
            <Typography variant="h4" component="h1">
              Grafos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de grafos del sistema
            </Typography>
          </Box>

          <Box className="graphs-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar grafo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Nuevo Grafo
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={graphs}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewGraphModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditGraphModal
          open={createEditModalOpen}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          mode={modalMode}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteGraphDialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          graph={graphToDelete}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default Graphs;
